import { formatDateTimeIT } from "@/lib/formatters";
import { Link } from "react-router-dom";
import { toBusinessDateKey } from "@/lib/agendaView";

const statusLabels = {
  unread: "Da leggere",
  read: "Letto",
  resolved: "Risolto",
};

const priorityLabels = {
  high: "Alta",
  medium: "Media",
  low: "Bassa",
};

export function getSourceLabel(alert) {
  const source = alert?.source_type || "";
  const type = alert?.alert_type || "";

  if (source === "recovery" || type.includes("recovery")) {
    return "Recupero Slot";
  }
  if (source === "reminder" || type.includes("reminder")) {
    return "Promemoria";
  }
  if (source === "public_booking" || type.startsWith("public_booking_")) {
    return "Prenotazione online";
  }
  return "Sistema";
}

export function getRequiredAction(alert) {
  const source = alert?.source_type || "";
  const type = alert?.alert_type || "";

  if (source === "recovery" || type.includes("recovery")) {
    return "Apri il recupero dello slot e decidi se serve un contatto manuale.";
  }
  if (source === "reminder" || type.includes("reminder")) {
    return "Verifica l’appuntamento e contatta il cliente se serve una conferma.";
  }
  if (source === "public_booking" || type.startsWith("public_booking_")) {
    if (type === "public_booking_contact_mismatch") {
      return "Verifica il recapito prima di aggiornare l’anagrafica del cliente.";
    }
    if (type === "public_booking_contact_conflict") {
      return "Verifica l’anagrafica prima di aggiornare i recapiti del cliente.";
    }
    return "Verifica l’anagrafica del cliente prima di apportare modifiche ai recapiti.";
  }
  return "Verifica il contesto dell’avviso e registra l’esito quando è gestito.";
}

export function getAlertDestination(alert) {
  const source = alert?.source_type || "";
  const type = alert?.alert_type || "";
  if (source === "recovery" || type.includes("recovery")) {
    return {
      to: alert.cancellation_id
        ? `/recovery?event=${encodeURIComponent(alert.cancellation_id)}`
        : "/recovery?status=expired",
      label: "Apri Recupero Slot",
    };
  }

  const startTime = alert?.metadata?.appointment_start_time;
  const opensAppointmentInAgenda = source === "reminder"
    || type.includes("reminder")
    || source === "public_booking"
    || type.startsWith("public_booking_");
  if (opensAppointmentInAgenda && alert.appointment_id && startTime) {
    const dateKey = toBusinessDateKey(new Date(startTime));
    return {
      to: `/?date=${encodeURIComponent(dateKey)}&appointment=${encodeURIComponent(alert.appointment_id)}`,
      label: "Apri in Agenda",
    };
  }
  return null;
}

function getBadgeClass(kind, value) {
  const classes = {
    source: "bg-slate-100 text-slate-700 border-slate-200",
    status: "bg-emerald-50 text-emerald-700 border-emerald-200",
  };
  if (kind === "priority") {
    if (value === "high") {
      return "bg-rose-50 text-rose-700 border-rose-200";
    }
    if (value === "low") {
      return "bg-slate-50 text-slate-600 border-slate-200";
    }
    return "bg-orange-50 text-orange-700 border-orange-200";
  }
  return classes[kind] || classes.source;
}

function Badge({ children, kind, value }) {
  return (
    <span className={`inline-flex items-center rounded-sm border px-2 py-1 text-xs font-medium ${getBadgeClass(kind, value)}`}>
      {children}
    </span>
  );
}

export function OwnerAlertCard({ alert, actionLoading, onMarkRead, onResolve }) {
  const isUnread = alert.status === "unread";
  const isResolved = alert.status === "resolved";
  const isBusy = actionLoading === alert.id;
  const destination = getAlertDestination(alert);

  return (
    <article
      className="bg-white border border-slate-200 rounded-sm p-4 md:p-5 space-y-4"
      data-testid={`owner-alert-card-${alert.id}`}
    >
      <div className="space-y-3">
        <div className="flex flex-wrap gap-2">
          <Badge kind="source">{getSourceLabel(alert)}</Badge>
          <Badge kind="priority" value={alert.priority}>{priorityLabels[alert.priority] || alert.priority || "Media"}</Badge>
          <Badge kind="status">{statusLabels[alert.status] || alert.status}</Badge>
        </div>
        <div>
          <h3 className="text-base font-semibold section-title" data-testid={`owner-alert-title-${alert.id}`}>
            {alert.title}
          </h3>
          <p className="mt-2 text-sm text-slate-600" data-testid={`owner-alert-message-${alert.id}`}>
            {alert.message}
          </p>
        </div>
        {alert.created_at && (
          <p className="text-xs text-slate-400" data-testid={`owner-alert-created-at-${alert.id}`}>
            Creato il {formatDateTimeIT(alert.created_at)}
          </p>
        )}
        <div className="rounded-sm border border-amber-200 bg-amber-50 p-3" data-testid={`owner-alert-required-action-${alert.id}`}>
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-800">Azione richiesta</p>
          <p className="mt-1 text-sm text-amber-900">{getRequiredAction(alert)}</p>
        </div>
        {destination && (
          <Link
            to={destination.to}
            className="inline-flex rounded-sm border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-900"
            data-testid={`owner-alert-open-${alert.id}`}
          >
            {destination.label}
          </Link>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-2">
        {isUnread && (
          <button
            type="button"
            onClick={() => onMarkRead(alert.id)}
            disabled={isBusy}
            className="bg-white border border-slate-200 text-slate-900 rounded-sm px-3 py-2 text-sm font-medium disabled:opacity-60"
            data-testid={`owner-alert-read-${alert.id}`}
          >
            {isBusy ? "Aggiornamento..." : "Ho visto"}
          </button>
        )}
        {!isResolved && (
          <button
            type="button"
            onClick={() => onResolve(alert.id)}
            disabled={isBusy}
            className="bg-slate-900 text-white rounded-sm px-3 py-2 text-sm font-medium disabled:opacity-60"
            data-testid={`owner-alert-resolve-${alert.id}`}
          >
            {isBusy ? "Aggiornamento..." : "Gestito"}
          </button>
        )}
      </div>
    </article>
  );
}
